import { Typography } from '@mui/material'
import { Controller } from 'react-hook-form'

import { UserRoleCodes } from '@saas/interfaces/core'
import useFetchUserRoles from '../../../hooks/useFetchUserRoles'
import useUserPermissions from '../../../hooks/useUserPermissions'
import { ButtonSelectorOption } from '../../Common/buttonSelector/_props'
import FlexBox from '../../Common/flexBox/FlexBox'
import UserFormField from '../UserFormField'
import { useFormContextUser } from '../utils/_utils'
import { roleDescriptions, roleLabels, userRoleIconsMap } from '../utils/userRolesUtils'
import { HcsDelegateRoleRefIds } from '@toktokdoctech/tokapi-client'

const UserRole = () => {
    const { control, watch } = useFormContextUser()
    const { hasUserPermission } = useUserPermissions()

    const { isLoading, error, hcsDelegateRoles } = useFetchUserRoles()

    const hcsDelegateRolesOptions = hcsDelegateRoles
        ?.reduce<ButtonSelectorOption<HcsDelegateRoleRefIds>[]>((prev, curr) => {
            if (curr && curr.name) {
                prev.push({
                    label: curr.name,
                    value: curr,
                })
            }

            return prev
        }, [])
        .filter(
            (role) =>
                !(Number(role.value.code) === UserRoleCodes.SUPERUSER && !hasUserPermission('SUPER ADMIN')) &&
                role.value.code !== watch('role')?.code,
        )

    if (isLoading) {
        return <UserFormField variant="input" label="Rôle attribué" required isLoading name="role" />
    }

    const hasError = !hcsDelegateRolesOptions || hcsDelegateRolesOptions.length === 0 || error

    if (hasError) return null

    return (
        <Controller
            control={control}
            name="role"
            rules={{
                required:
                    'Cette information est obligatoire et n’a pas été renseignée. Vous pouvez vous aider des informations disponibles (en survolant le symbole “information”) pour choisir le rôle',
            }}
            render={({ field, fieldState }) => {
                const isUserAllowed = !hasUserPermission('SUPER ADMIN') && field.value?.code === UserRoleCodes.SUPERUSER

                const tooltipTitle =
                    "Champ non modifiable, car l'utilisateur dispose d'un compte aux privilèges plus élevés"

                return (
                    <UserFormField
                        variant="select"
                        type="default"
                        name={field.name}
                        label="Rôle attribué"
                        status={{
                            error: fieldState.error,
                            valid: Boolean(field.value),
                        }}
                        required
                        locked={isUserAllowed}
                        readonly={isUserAllowed}
                        options={hcsDelegateRolesOptions}
                        value={field.value}
                        startAdornment={
                            typeof field?.value?.code === 'number' ? userRoleIconsMap[field?.value?.code] : undefined
                        }
                        renderLabel={() => roleLabels[field?.value?.code ?? UserRoleCodes.SIMPLE] ?? undefined}
                        placeholder="Choisir"
                        onChange={field.onChange}
                        renderOption={(option) => (
                            <FlexBox gap="1rem" alignItems="center">
                                {typeof option?.code === 'number' ? userRoleIconsMap[option.code] : null}
                                <Typography>{roleLabels[option.code ?? UserRoleCodes.SIMPLE]}</Typography>
                            </FlexBox>
                        )}
                        dataCyScope="UserField"
                        tooltipTitle={tooltipTitle}
                        sideTooltip={
                            !isUserAllowed && (
                                <ul>
                                    <FlexBox flexDirection="column" gap=".3rem">
                                        {hcsDelegateRoles?.map((role) => (
                                            <li key={role.code}>
                                                <strong>{role.name}</strong>
                                                {` : ${roleDescriptions[role.code ?? UserRoleCodes.SIMPLE]}`}
                                            </li>
                                        ))}
                                    </FlexBox>
                                </ul>
                            )
                        }
                    />
                )
            }}
        />
    )
}

export default UserRole
